import type { ShopperProducts } from '@/scapi';
import type { ContentFolder } from '@/lib/api/content-folders.server';

type Category = ShopperProducts.schemas['Category'];

const EXTERNAL_URL_RE = /^https?:\/\//i;

function hasControlCharacter(value: string): boolean {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 31 || code === 127) {
            return true;
        }
    }

    return false;
}

/** http(s) menu destination. Any other value is ignored so it cannot unpublish a real page. */
export function getMenuExternalUrl(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }

    const url = value.trim();
    if (!url || hasControlCharacter(url) || !EXTERNAL_URL_RE.test(url)) {
        return undefined;
    }

    return url;
}

/** One customer group id or a list. Empty means the menu row is visible to every shopper. */
export function readCustomerGroupIds(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.flatMap((id) => (typeof id === 'string' && id.trim() ? [id.trim()] : []));
    }

    if (typeof value === 'string' && value.trim()) {
        return [value.trim()];
    }

    return [];
}

/**
 * A shopper sees the row when it lists no groups, or when they belong to any listed group.
 * `membership` is the set of groups the shopper qualified for. An empty set hides every restricted row.
 */
export function isVisibleForCustomerGroups(groupIds: readonly string[], membership: ReadonlySet<string>): boolean {
    if (groupIds.length === 0) {
        return true;
    }

    return groupIds.some((id) => membership.has(id));
}

function walkCategories(categories: Category[] | undefined, ids: Set<string>): void {
    for (const category of categories ?? []) {
        for (const id of readCustomerGroupIds(category.c_customerGroupId)) {
            ids.add(id);
        }
        walkCategories(category.categories, ids);
    }
}

function walkFolders(folders: ContentFolder[] | undefined, ids: Set<string>): void {
    for (const folder of folders ?? []) {
        for (const id of folder.customerGroupId ?? []) {
            if (id.trim()) {
                ids.add(id.trim());
            }
        }
        walkFolders(folder.folders, ids);
    }
}

export function collectCustomerGroupIds(categories: Category[] | undefined, folders: ContentFolder[]): string[] {
    const ids = new Set<string>();
    walkCategories(categories, ids);
    walkFolders(folders, ids);
    return [...ids];
}

export function filterCategoriesByCustomerGroup(
    categories: Category[] | undefined,
    membership: ReadonlySet<string>
): Category[] {
    return (categories ?? []).flatMap((category) => {
        if (!isVisibleForCustomerGroups(readCustomerGroupIds(category.c_customerGroupId), membership)) {
            return [];
        }

        if (!category.categories) {
            return [category];
        }

        return [
            {
                ...category,
                categories: filterCategoriesByCustomerGroup(category.categories, membership),
            },
        ];
    });
}

export function filterContentFoldersByCustomerGroup(
    folders: ContentFolder[],
    membership: ReadonlySet<string>
): ContentFolder[] {
    return folders.flatMap((folder) => {
        if (!isVisibleForCustomerGroups(folder.customerGroupId ?? [], membership)) {
            return [];
        }

        if (!folder.folders) {
            return [folder];
        }

        return [
            {
                ...folder,
                folders: filterContentFoldersByCustomerGroup(folder.folders, membership),
            },
        ];
    });
}
